import gsap from 'gsap';

class MobileMenu {
    private button: HTMLElement | null;
    private panel: HTMLElement | null;
    private header: HTMLElement | null;
    private line1: HTMLElement | null;
    private line2: HTMLElement | null;
    private line3: HTMLElement | null;
    private isOpen = false;
    private lockedScrollY = 0;

    constructor() {
        this.button = document.getElementById('mobile-menu-toggle');
        this.panel = document.getElementById('mobile-menu-panel');
        this.header = document.querySelector('header');
        this.line1 = document.getElementById('hamburger-line-1');
        this.line2 = document.getElementById('hamburger-line-2');
        this.line3 = document.getElementById('hamburger-line-3');

        if (!this.button || !this.panel) {
            console.error('Mobile menu elements not found');
            return;
        }

        this.init();
    }

    private init(): void {
        if (!this.button || !this.panel) return;

        this.button.addEventListener('click', () => this.toggle());

        // Close menu when clicking on menu links
        const menuLinks = this.panel.querySelectorAll('a');
        menuLinks.forEach(link => {
            link.addEventListener('click', () => this.close());
        });

        // Initialize submenu toggles
        const submenuToggles = this.panel.querySelectorAll('.mobile-submenu-toggle');
        submenuToggles.forEach(toggle => {
            toggle.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleSubmenu(toggle as HTMLElement);
            });
        });

        // Close menu when clicking outside
        document.addEventListener('click', (e) => {
            const target = e.target as Node;
            const mobileNav = document.getElementById('mobilemenu');

            if (this.isOpen && mobileNav && !mobileNav.contains(target)) {
                this.close();
            }
        });

        // Close menu when pressing Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
    }

    private toggleSubmenu(toggle: HTMLElement): void {
        const submenu = toggle.closest('.mobile-submenu');
        if (!submenu) return;

        const content = submenu.querySelector('.mobile-submenu-content');
        const icon = toggle.querySelector('.mobile-submenu-icon');
        const link = toggle.previousElementSibling as HTMLElement | null;
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
            // Close submenu
            content?.classList.add('hidden');
            toggle.setAttribute('aria-expanded', 'false');
            link?.classList.remove('text-primary-bold');
            if (icon) {
                (icon as HTMLElement).style.transform = 'rotate(0deg)';
            }
        } else {
            // Open submenu
            content?.classList.remove('hidden');
            content?.classList.add('flex', 'flex-col', 'gap-4');
            toggle.setAttribute('aria-expanded', 'true');
            link?.classList.add('text-primary-bold');
            if (icon) {
                (icon as HTMLElement).style.transform = 'rotate(180deg)';
            }
        }
    }

    private toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    private open(): void {
        if (!this.panel || !this.button) return;

        this.isOpen = true;
        this.panel.classList.remove('hidden');
        this.button.setAttribute('aria-expanded', 'true');

        // Close any open dropdowns first
        const openDropdowns = document.querySelectorAll('.dropdown-content:not(.hidden)');
        openDropdowns.forEach(dropdown => {
            dropdown.classList.add('hidden');
            const toggle = dropdown.parentElement?.querySelector('.dropdown-toggle');
            if (toggle) {
                toggle.setAttribute('aria-expanded', 'false');
            }
        });

        // Expand header to full height
        if (this.header) {
            console.log('Header before:', this.header.className);
            this.header.classList.remove('h-auto');
            this.header.classList.add('h-screen');
            console.log('Header after:', this.header.className);
        } else {
            console.error('Header element not found!');
        }

        // Animate hamburger to X
        //this.animateToX();

        // Animate panel entrance, then lock scroll to avoid visible page jump
        gsap.fromTo(
            this.panel,
            { opacity: 0 },
            { opacity: 1, duration: 0.3, ease: 'power2.out', onComplete: () => this.lockBodyScroll() }
        );
    }

    private close(): void {
        if (!this.panel || !this.button) return;

        this.isOpen = false;
        this.button.setAttribute('aria-expanded', 'false');

        // Close all open submenus
        const openSubmenus = this.panel.querySelectorAll('.mobile-submenu-content:not(.hidden)');
        openSubmenus.forEach(content => {
            content.classList.add('hidden');
            const submenu = content.closest('.mobile-submenu');
            const toggle = submenu?.querySelector('.mobile-submenu-toggle');
            if (toggle) {
                toggle.setAttribute('aria-expanded', 'false');
                const link = toggle.previousElementSibling as HTMLElement | null;
                link?.classList.remove('text-primary-bold');
                const icon = toggle.querySelector('.mobile-submenu-icon');
                if (icon) {
                    (icon as HTMLElement).style.transform = 'rotate(0deg)';
                }
            }
        });

        // Animate X back to hamburger
        //this.animateToHamburger();

        // Animate panel exit
        gsap.to(this.panel, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.in',
            onComplete: () => {
                this.panel?.classList.add('hidden');

                // Restore header to normal height after animation completes
                if (this.header) {
                    this.header.classList.remove('h-screen');
                    this.header.classList.add('h-auto');
                }
            }
        });

        // Restore body scroll
        this.unlockBodyScroll();
    }

    private lockBodyScroll(): void {
        this.lockedScrollY = window.scrollY;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.lockedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
    }

    private unlockBodyScroll(): void {
        const y = this.lockedScrollY;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        window.scrollTo({ top: y, behavior: 'instant' as ScrollBehavior });
    }

    private animateToX(): void {
        if (!this.line1 || !this.line2 || !this.line3) return;

        const timeline = gsap.timeline();

        // Line 2 (middle) fades out
        timeline.to(this.line2, {
            opacity: 0,
            duration: 0.2,
            ease: 'power2.in'
        });

        // Line 1 rotates and moves to center to form top part of X
        timeline.to(
            this.line1,
            {
                rotation: 45,
                transformOrigin: 'center center',
                top: '50%',
                y: '-50%',
                duration: 0.3,
                ease: 'power2.inOut'
            },
            '-=0.1'
        );

        // Line 3 rotates and moves to center to form bottom part of X
        timeline.to(
            this.line3,
            {
                rotation: -45,
                transformOrigin: 'center center',
                bottom: 'auto',
                top: '50%',
                y: '-50%',
                duration: 0.3,
                ease: 'power2.inOut'
            },
            '-=0.3'
        );
    }

    private animateToHamburger(): void {
        if (!this.line1 || !this.line2 || !this.line3) return;

        const timeline = gsap.timeline();

        // Line 1 rotates back and returns to top position
        timeline.to(
            this.line1,
            {
                rotation: 0,
                top: '25%',
                y: '-50%',
                duration: 0.3,
                ease: 'power2.inOut'
            }
        );

        // Line 3 rotates back and returns to bottom position
        timeline.to(
            this.line3,
            {
                rotation: 0,
                top: '75%',
                y: '-50%',
                duration: 0.3,
                ease: 'power2.inOut'
            },
            '-=0.3'
        );

        // Line 2 fades back in
        timeline.to(
            this.line2,
            {
                opacity: 1,
                duration: 0.2,
                ease: 'power2.out'
            },
            '-=0.1'
        );
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new MobileMenu();
});

export default MobileMenu;
